import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCADSheet } from '@/hooks/use-cad-sheet';
import { InputForm } from '@/components/cad/InputForm';
import { ImageGrid } from '@/components/cad/ImageGrid';
import { LivePreview } from '@/components/cad/LivePreview';
import { AllSheetsPanel } from '@/components/cad/AllSheetsPanel';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, Undo2, Redo2, FileText, PlusCircle, RefreshCw, Save, FilePlus2, X, Loader2, Link, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { LoadFromTaskButton, mapTaskToSheet } from '@/components/cad/LoadFromTaskButton';
import { syncCadToTask, getTaskByStyleNo, updateTaskStatus, getTask, getCadSheet, pushCADToPortal } from '@/utils/api';
import { storeSheet, deserializeSheet } from '@/lib/store-sheet';
import { renderSheetToPng } from '@/lib/render-sheet-png';
import { renderViewToDataURL } from '@/lib/canvas-export';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function CADSheetPage() {
  const sheet = useCADSheet();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('editor');
  const [linkedTaskId, setLinkedTaskId] = useState(null);
  const [linkedTaskMeta, setLinkedTaskMeta] = useState(null); // { import_source, style_no, ... }
  const [syncing, setSyncing] = useState(false);
  // Save popup state — `mode` is 'save' (overwrite) or 'saveAs' (new sheet doc + new version).
  const [showSavePopup, setShowSavePopup] = useState(false);
  const [saveMode, setSaveMode] = useState('save');
  const [cadUrl, setCadUrl] = useState('');
  const [markCompleted, setMarkCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pushingPortal, setPushingPortal] = useState(false);

  const handleEditSheet = useCallback((data, id, taskIdOverride) => {
    // `data` may carry `_linkedTaskId` from deserializeSheet; the explicit `taskIdOverride`
    // wins so callers can pass it explicitly when known.
    const taskId = taskIdOverride ?? data?._linkedTaskId ?? null;
    sheet.loadSheet(data);
    setEditingId(id);
    setLinkedTaskId(taskId);
    setActiveTab('editor');
  }, [sheet]);

  const handleNewSheet = useCallback(() => {
    sheet.reset();
    if (user?.name) {
      sheet.updateField('designerName', user.name);
    }
    setEditingId(undefined);
    setLinkedTaskId(null);
    setLinkedTaskMeta(null);
    setActiveTab('editor');
  }, [sheet, user]);

  // When loading from task, capture the task ID for sync
  const handleLoadFromTask = useCallback((data, taskId) => {
    const updatedData = { ...data };
    if (!updatedData.designerName && user?.name) {
      updatedData.designerName = user.name;
    }
    sheet.loadSheet(updatedData);
    setEditingId(undefined);
    setLinkedTaskId(taskId || null);
  }, [sheet, user]);

  // Deep-link: /cad-sheets?edit=<sheetId> opens a saved sheet for editing.
  // Triggered by TaskSummaryView's "Open in Editor" button — without this, that
  // link landed on a blank canvas, which is what made saved CADs look "empty".
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || editId === editingId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getCadSheet(editId);
        if (cancelled || !res?.data) return;
        const data = deserializeSheet(res.data);
        handleEditSheet(data, editId, data._linkedTaskId);
        // Strip the query so refreshes don't re-load.
        const next = new URLSearchParams(searchParams);
        next.delete('edit');
        setSearchParams(next, { replace: true });
      } catch (err) {
        if (!cancelled) toast.error('Could not load CAD sheet from link');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('edit')]);

  // Deep-link: /cad-sheets?task=<taskId> preloads details from a task.
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (!taskId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getTask(taskId);
        if (cancelled || !res?.data) return;
        const data = mapTaskToSheet(res.data);
        handleLoadFromTask(data, taskId);
        // Strip the query so refreshes don't re-load.
        const next = new URLSearchParams(searchParams);
        next.delete('task');
        setSearchParams(next, { replace: true });
        toast.success(`Pre-loaded details from task: ${res.data.style_no || res.data.internal_cad_no}`);
      } catch (err) {
        if (!cancelled) toast.error('Could not load task details');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('task')]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (activeTab !== 'editor') return;
      const isCmd = e.ctrlKey || e.metaKey;
      if (!isCmd) return;
      
      const key = e.key.toLowerCase();
      if (key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          if (sheet.canRedo()) sheet.redo();
        } else {
          if (sheet.canUndo()) sheet.undo();
        }
      } else if (key === 'y') {
        e.preventDefault();
        if (sheet.canRedo()) sheet.redo();
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [activeTab, sheet]);

  // Auto-set designer name on mount / when user changes if not already set
  useEffect(() => {
    if (user?.name && !sheet.data.designerName && !editingId && !linkedTaskId) {
      sheet.updateField('designerName', user.name);
    }
  }, [user, sheet, editingId, linkedTaskId, sheet.data.designerName]);

  // Whenever the linked task changes, fetch its metadata so we can show the SGI
  // import-source badge and gate the "Push to SGI" action accordingly.
  useEffect(() => {
    if (!linkedTaskId) { setLinkedTaskMeta(null); return; }
    let cancelled = false;
    getTask(linkedTaskId)
      .then(res => { if (!cancelled) setLinkedTaskMeta(res?.data || null); })
      .catch(() => { if (!cancelled) setLinkedTaskMeta(null); });
    return () => { cancelled = true; };
  }, [linkedTaskId]);

  // Open Save popup. 'save' overwrites the existing sheet (or creates the first one
  // for a fresh sheet — storeSheet handles both via the saveAs flag). 'saveAs' always
  // creates a new sheet doc + new cad_versions entry.
  const handleSaveClick = useCallback(() => {
    setSaveMode('save');
    setCadUrl('');
    setMarkCompleted(false);
    setShowSavePopup(true);
  }, []);

  const handleSaveAsClick = useCallback(() => {
    setSaveMode('saveAs');
    setCadUrl('');
    setMarkCompleted(false);
    setShowSavePopup(true);
  }, []);

  // Execute save: store sheet + link to task + optional status change.
  // Save (saveMode='save') overwrites the open sheet; backend updates the matching
  // cad_versions entry on the task in place. Save As (saveMode='saveAs') always
  // POSTs a fresh sheet, so the task gets a new cad_versions entry.
  const handleConfirmSave = useCallback(async () => {
    setSaving(true);
    try {
      // Resolve the linked task up front so the backend can sync without falling
      // back to fuzzy style_no matching (and so we can pass linked_task_id along
      // with the POST/PUT for saveAs flows where editingId resets).
      let taskId = linkedTaskId;
      if (!taskId && sheet.data.styleNo) {
        try {
          const res = await getTaskByStyleNo(sheet.data.styleNo);
          taskId = res.data?.id || null;
        } catch { /* no matching task — proceed without one */ }
      }

      // Render a small consolidated thumbnail so the task summary shows the layout
      // instead of the first raw view. Uses the lightweight JPEG preset to keep Save
      // fast — full-quality PNG is reserved for Export and Push-to-SGI. Failures and
      // empty-sheet cases are non-fatal: the save proceeds without a thumbnail.
      let consolidatedImageBase64 = '';
      try {
        const thumb = await renderSheetToPng(sheet.data, { mode: 'thumbnail' });
        if (thumb) consolidatedImageBase64 = thumb;
      } catch (err) {
        console.warn('Consolidated thumbnail render failed; falling back to first-view image:', err);
      }

      const result = await storeSheet(sheet.data, editingId, {
        linkedTaskId: taskId || undefined,
        consolidatedImageBase64,
        saveAs: saveMode === 'saveAs',
      });
      if (!result.success) {
        toast.error('Failed to save CAD sheet');
        setSaving(false);
        return;
      }
      const savedId = result.id;
      setEditingId(savedId);
      setRefreshKey(k => k + 1);

      if (taskId) {
        // Sync CAD data back to task
        const updateData = {};
        if (sheet.data.styleNo) updateData.style_no = sheet.data.styleNo;
        if (sheet.data.customerName) updateData.client_name = sheet.data.customerName;
        if (sheet.data.size) updateData.size = sheet.data.size;
        if (sheet.data.netWeight) updateData.net_weight = parseFloat(sheet.data.netWeight) || null;
        if (sheet.data.grossWeight) updateData.gross_weight = parseFloat(sheet.data.grossWeight) || null;
        if (sheet.data.finishing) updateData.finishing = sheet.data.finishing;
        if (sheet.data.engravingInstructions) updateData.engraving = sheet.data.engravingInstructions;
        if (sheet.data.engravingImage?.url) updateData.engraving_image = sheet.data.engravingImage.url;
        if (sheet.data.stampingInstructions) updateData.stamping_instructions = sheet.data.stampingInstructions;
        if (sheet.data.stampingImages?.length) updateData.stamping_images = sheet.data.stampingImages.map(i => i.url).filter(Boolean);
        if (sheet.data.otherDetails) updateData.special_instructions = sheet.data.otherDetails;
        if (sheet.data.ksNotes) updateData.ks_notes = sheet.data.ksNotes;
        if (sheet.data.designerName) updateData.designer_name = sheet.data.designerName;
        if (sheet.data.metals?.length > 0) {
          const m = sheet.data.metals[0];
          updateData.metal_karat = m.karat ? (m.karat.includes('K') ? m.karat : `${m.karat}K`) : null;
          updateData.metal_colour = m.color || null;
          updateData.metal_type = m.metal_type || 'gold';
          if (!updateData.net_weight && m.netWeight) updateData.net_weight = parseFloat(m.netWeight) || null;
          updateData.metal_details = sheet.data.metals.map(mt => ({
            metal_type: mt.metal_type || 'gold',
            metal_colour: mt.color || 'Yellow',
            metal_karat: mt.karat ? (mt.karat.includes('K') ? mt.karat : `${mt.karat}K`) : '14K',
            weight: parseFloat(mt.netWeight) || null,
          }));
        }
        if (sheet.data.stones?.length > 0) {
          updateData.diamonds = sheet.data.stones.map(s => ({
            category: s.category || 'Accent Stone',
            stone_category: s.stone_category || 'Natural',
            ownership: s.ownership || 'KS Goods',
            stone_shape: s.stone_shape || s.type || 'Round',
            stone_quality: s.stone_quality || s.quality || '',
            setting_type: s.setting_type || 'Prong',
            size_mm: s.size_mm || s.size || '',
            weight_per_stone: parseFloat(s.weight_per_stone ?? s.weight) || 0,
            quantity: parseInt(s.quantity) || 0,
            total_weight: (parseFloat(s.weight_per_stone ?? s.weight) || 0) * (parseInt(s.quantity) || 0),
          }));
        }
        // Save the CAD URL to the task
        if (cadUrl.trim()) {
          updateData.cad_url = cadUrl.trim();
        }

        try {
          await syncCadToTask(taskId, updateData);
        } catch (err) {
          console.error('Sync to task failed:', err);
        }

        // 3. Optionally change status to CAD Completed
        if (markCompleted) {
          try {
            await updateTaskStatus(taskId, { status: 'cad_completed' });
            toast.success('Status changed to CAD Completed');
          } catch (err) {
            toast.error('Could not change status: ' + (err.response?.data?.detail || err.message));
          }
        }
      }

      const verb = saveMode === 'saveAs'
        ? 'CAD sheet saved as new version'
        : (editingId ? 'CAD sheet updated' : 'CAD sheet saved');
      toast.success(taskId ? `${verb} & linked` : verb);
      setShowSavePopup(false);
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }, [sheet.data, editingId, linkedTaskId, cadUrl, markCompleted, saveMode]);

  // Push the current sheet's images (per-view PNGs + consolidated) to the SGI portal
  // as CAD images on the source order. Mirrors LivePreview.handlePushToPortal so the
  // action is one click from the editor toolbar without opening the preview modal.
  const handlePushToSgi = useCallback(async () => {
    if (pushingPortal) return;
    if (!linkedTaskId && !sheet.data.styleNo && !sheet.data.customerDesignNo) {
      toast.error('Load a task or fill Style No first.');
      return;
    }
    if (linkedTaskMeta && !linkedTaskMeta.import_source?.portal_id) {
      toast.error('This task was not imported from SGI — re-import it from the portal first.');
      return;
    }
    setPushingPortal(true);
    try {
      const prefix = (sheet.data.styleNo || sheet.data.customerDesignNo || 'CAD-Sheet').replace(/[^a-zA-Z0-9._-]/g, '_');
      const metadata = {
        partyDesignNo: sheet.data.customerDesignNo || '',
        styleNo: sheet.data.styleNo || '',
        pieceName: sheet.data.jewelryType || '',
      };
      const images = [];
      for (let i = 0; i < sheet.data.images.length; i++) {
        const img = sheet.data.images[i];
        if (!img.url) continue;
        try {
          const viewDataUrl = await renderViewToDataURL(img, metadata);
          const safeLabel = (img.label || `view-${i + 1}`).replace(/[^a-zA-Z0-9]/g, '_');
          images.push({ data_url: viewDataUrl, filename: `${prefix}_${safeLabel}.png`, notes: `View: ${img.label || ''}`.trim() });
        } catch (e) { console.error(`Failed to render view ${img.label}:`, e); }
      }
      try {
        // Push-to-SGI uses export-quality PNG so what the customer sees matches the ZIP export.
        const consolidatedUrl = await renderSheetToPng(sheet.data, { mode: 'export' });
        if (consolidatedUrl) {
          images.push({ data_url: consolidatedUrl, filename: `${prefix}_Consolidated.png`, notes: 'Consolidated CAD sheet' });
        }
      } catch (e) { console.error('Consolidated render failed:', e); }

      if (images.length === 0) { toast.error('Nothing to upload — sheet has no images.'); return; }

      toast.info(`Uploading ${images.length} image(s) to SGI portal…`);
      const res = await pushCADToPortal({
        task_id: linkedTaskId || undefined,
        style_no: sheet.data.styleNo || undefined,
        customer_design_no: sheet.data.customerDesignNo || undefined,
        images,
      });
      const uploaded = res?.data?.uploaded ?? 0;
      const total = res?.data?.total ?? images.length;
      const ok = res?.data?.success;
      // Pull the first per-file failure message — `results` is an array of
      // {success, filename, message} from the backend uploader. Without
      // surfacing this, the operator only sees "SGI upload failed" with no
      // hint whether it was login, network, or the portal rejecting the file.
      const firstFail = (res?.data?.results || []).find(r => !r.success);
      const errSummary = res?.data?.error || firstFail?.message || 'unknown reason';
      if (ok && uploaded === total) toast.success(`Pushed ${uploaded} image(s) to SGI portal.`);
      else if (uploaded > 0) toast.warning(`Pushed ${uploaded} of ${total} — ${errSummary}`, { duration: 10000 });
      else toast.error(`SGI upload failed: ${errSummary}`, { duration: 12000 });
    } catch (err) {
      console.error('Push to SGI failed:', err);
      toast.error(`Could not push to SGI: ${err?.response?.data?.detail || err?.message || 'unknown error'}`, { duration: 10000 });
    } finally {
      setPushingPortal(false);
    }
  }, [sheet.data, linkedTaskId, linkedTaskMeta, pushingPortal]);

  // Sync CAD sheet data back to the task (quick sync without save popup)
  const handleSyncToTask = useCallback(async () => {
    const styleNo = sheet.data.styleNo;
    if (!styleNo && !linkedTaskId) {
      toast.error('No linked task. Load a task first or enter a Style No.');
      return;
    }
    setSyncing(true);
    try {
      let taskId = linkedTaskId;
      if (!taskId && styleNo) {
        const res = await getTaskByStyleNo(styleNo);
        taskId = res.data.id;
      }
      if (!taskId) {
        toast.error('Could not find linked task');
        setSyncing(false);
        return;
      }
      const updateData = {};
      if (sheet.data.styleNo) updateData.style_no = sheet.data.styleNo;
      if (sheet.data.customerName) updateData.client_name = sheet.data.customerName;
      if (sheet.data.size) updateData.size = sheet.data.size;
      if (sheet.data.netWeight) updateData.net_weight = parseFloat(sheet.data.netWeight) || null;
      if (sheet.data.grossWeight) updateData.gross_weight = parseFloat(sheet.data.grossWeight) || null;
      if (sheet.data.finishing) updateData.texture = sheet.data.finishing;
      if (sheet.data.engravingInstructions) updateData.engraving = sheet.data.engravingInstructions;
      if (sheet.data.otherDetails) updateData.special_instructions = sheet.data.otherDetails;
      if (sheet.data.ksNotes) updateData.ks_notes = sheet.data.ksNotes;
      if (sheet.data.designerName) updateData.designer_name = sheet.data.designerName;
      if (sheet.data.metals?.length > 0) {
        const m = sheet.data.metals[0];
        updateData.metal_karat = m.karat ? (m.karat.includes('K') ? m.karat : `${m.karat}K`) : null;
        updateData.metal_colour = m.color || null;
        updateData.metal_type = m.metal_type || 'gold';
        if (!updateData.net_weight && m.netWeight) updateData.net_weight = parseFloat(m.netWeight) || null;
        updateData.metal_details = sheet.data.metals.map(mt => ({
          metal_type: mt.metal_type || 'gold',
          metal_colour: mt.color || 'Yellow',
          metal_karat: mt.karat ? (mt.karat.includes('K') ? mt.karat : `${mt.karat}K`) : '14K',
          weight: parseFloat(mt.netWeight) || null,
        }));
      }
      if (sheet.data.stones?.length > 0) {
        updateData.diamonds = sheet.data.stones.map(s => ({
          category: s.category || 'Accent Stone',
          stone_category: s.stone_category || 'Natural',
          ownership: s.ownership || 'KS Goods',
          stone_shape: s.stone_shape || s.type || 'Round',
          stone_quality: s.stone_quality || s.quality || '',
          setting_type: s.setting_type || 'Prong',
          size_mm: s.size_mm || s.size || '',
          weight_per_stone: parseFloat(s.weight_per_stone ?? s.weight) || 0,
          quantity: parseInt(s.quantity) || 0,
          total_weight: (parseFloat(s.weight_per_stone ?? s.weight) || 0) * (parseInt(s.quantity) || 0),
        }));
      }
      await syncCadToTask(taskId, updateData);
      toast.success('Task updated from CAD sheet data');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync to task');
    } finally {
      setSyncing(false);
    }
  }, [sheet.data, linkedTaskId]);

  return (
    <CollapsibleLayout>
      <div className="flex flex-col h-[calc(100vh-48px)]" data-testid="cad-sheet-page">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <img src="/ks-logo.png" alt="K's" className="h-7 w-7 object-contain" />
            <h1 className="text-base font-bold text-primary tracking-wide">CAD Sheet Designer</h1>
            <div className="flex border border-border rounded-md overflow-hidden ml-4">
              <button
                onClick={() => setActiveTab('editor')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${activeTab === 'editor' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                data-testid="cad-tab-editor"
              >
                <PlusCircle className="h-3 w-3 inline mr-1" />Editor
              </button>
              <button
                onClick={() => setActiveTab('sheets')}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${activeTab === 'sheets' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
                data-testid="cad-tab-sheets"
              >
                <FileText className="h-3 w-3 inline mr-1" />All Sheets
              </button>
            </div>
          </div>
          {activeTab === 'editor' && (
            <div className="flex items-center gap-2">
              {/* Linked-task badge: shows whether the loaded task came from SGI portal so
                  the user knows up front whether "Push to SGI" will work. */}
              {linkedTaskId && linkedTaskMeta && (
                linkedTaskMeta.import_source?.portal_id ? (
                  <span className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="cad-sgi-linked-badge">
                    <CheckCircle2 className="h-3 w-3" />SGI #{linkedTaskMeta.import_source.order_no || '—'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid="cad-sgi-manual-badge" title="Task was not imported from SGI — Push to SGI is unavailable">
                    <AlertCircle className="h-3 w-3" />Manual task
                  </span>
                )
              )}
              {(linkedTaskId || sheet.data.styleNo) && (
                <Button size="sm" variant="outline" onClick={handleSyncToTask} disabled={syncing} className="h-7 text-xs" data-testid="cad-sync-task-btn">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : 'Sync to Task'}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} className="h-7 text-xs" data-testid="cad-preview-btn">
                <Eye className="h-3.5 w-3.5 mr-1" />Preview & Export
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handlePushToSgi}
                disabled={pushingPortal || (linkedTaskMeta && !linkedTaskMeta.import_source?.portal_id)}
                className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                title={linkedTaskMeta && !linkedTaskMeta.import_source?.portal_id ? 'Task is not linked to SGI' : 'Upload CAD images to SGI portal'}
                data-testid="cad-push-sgi-toolbar-btn"
              >
                {pushingPortal ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                Push to SGI
              </Button>
              <Button size="sm" onClick={handleSaveClick} className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="cad-save-main-btn">
                <Save className="h-3.5 w-3.5 mr-1" />{editingId ? 'Save' : 'Save & Link'}
              </Button>
              {editingId && (
                <Button size="sm" variant="outline" onClick={handleSaveAsClick} className="h-7 text-xs border-green-200 text-green-700 hover:bg-green-50" data-testid="cad-save-as-btn" title="Save as a new CAD version on this task">
                  <FilePlus2 className="h-3.5 w-3.5 mr-1" />Save As
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === 'editor' ? (
          <div className="flex-1 overflow-hidden flex">
            {/* Left Toolbar */}
            <div className="w-11 bg-muted/40 border-r border-border flex flex-col items-center py-3 gap-2 flex-shrink-0" data-testid="cad-left-toolbar">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={sheet.undo}
                disabled={!sheet.canUndo()}
                title="Undo"
                data-testid="cad-undo-btn"
              >
                <Undo2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={sheet.redo}
                disabled={!sheet.canRedo()}
                title="Redo"
                data-testid="cad-redo-btn"
              >
                <Redo2 className="h-4 w-4" />
              </Button>
              <div className="w-6 border-t border-border my-1" />
              <LoadFromTaskButton
                onLoadSheet={(data, taskId) => handleLoadFromTask(data, taskId)}
                compact
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleNewSheet}
                title="New Sheet"
                data-testid="cad-new-btn"
              >
                <PlusCircle className="h-4 w-4" />
              </Button>
            </div>

            {/* Left: Input Form */}
            <div className="w-[380px] border-r border-border overflow-y-auto p-4 flex-shrink-0">
              <InputForm
                data={sheet.data}
                onUpdateField={sheet.updateField}
                onAddStone={sheet.addStone}
                onImportStones={sheet.importStones}
                onUpdateStone={sheet.updateStone}
                onRemoveStone={sheet.removeStone}
                onAddMetal={sheet.addMetal}
                onUpdateMetal={sheet.updateMetal}
                onRemoveMetal={sheet.removeMetal}
                onAddFinding={sheet.addFinding}
                onUpdateFinding={sheet.updateFinding}
                onRemoveFinding={sheet.removeFinding}
                onSetGrossWeightManual={sheet.setGrossWeightManual}
                onSetEngravingImage={sheet.setEngravingImage}
                onRemoveEngravingImage={sheet.removeEngravingImage}
                onAddCustomerImage={sheet.addCustomerImage}
                onRemoveCustomerImage={sheet.removeCustomerImage}
                onAddStampingImage={sheet.addStampingImage}
                onRemoveStampingImage={sheet.removeStampingImage}
                onReset={sheet.reset}
              />
            </div>
            {/* Right: Image Grid */}
            <div className="flex-1 overflow-y-auto">
              <ImageGrid
                images={sheet.data.images}
                onSetImage={sheet.setImage}
                onRemoveImage={sheet.removeImage}
                onAddImageSlot={sheet.addImageSlot}
                onRemoveImageSlot={sheet.removeImageSlot}
                onUpdateLabel={sheet.updateImageLabel}
                onAddAnnotation={sheet.addAnnotation}
                onRemoveAnnotation={sheet.removeAnnotation}
                onUpdateAnnotation={sheet.updateAnnotation}
                onUpdateAnnotationInteractive={sheet.updateAnnotationInteractive}
                onAddTextBox={sheet.addTextBox}
                onRemoveTextBox={sheet.removeTextBox}
                onUpdateTextBox={sheet.updateTextBox}
                onUpdateTextBoxInteractive={sheet.updateTextBoxInteractive}
                onAddHighlight={sheet.addHighlight}
                onRemoveHighlight={sheet.removeHighlight}
                onUpdateHighlight={sheet.updateHighlight}
                onCommitHistory={sheet.commitHistory}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden">
            <AllSheetsPanel onEditSheet={handleEditSheet} refreshKey={refreshKey} />
          </div>
        )}

        {/* Preview Modal — export only, no save inside */}
        <LivePreview
          data={sheet.data}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          taskId={linkedTaskId}
        />

        {/* Save & Link Popup */}
        {showSavePopup && (
          <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center" data-testid="cad-save-popup-overlay">
            <div className="bg-card border border-border rounded-xl shadow-2xl w-[420px] p-6" data-testid="cad-save-popup">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                  {saveMode === 'saveAs' ? <FilePlus2 className="h-4 w-4 text-green-600" /> : <Save className="h-4 w-4 text-green-600" />}
                  {saveMode === 'saveAs' ? 'Save As New CAD Version' : (editingId ? 'Save CAD Sheet' : 'Save & Link CAD Sheet')}
                </h3>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowSavePopup(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* CAD URL Input */}
                <div>
                  <Label className="text-xs font-medium">CAD File URL <span className="text-muted-foreground">(local DB location)</span></Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Link className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <Input
                      value={cadUrl}
                      onChange={e => setCadUrl(e.target.value)}
                      placeholder="e.g. Z:\CAD Files\R2-216680.3dm"
                      className="h-8 text-sm"
                      data-testid="cad-url-input"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Where this CAD file is saved on your local system/network</p>
                </div>

                {/* Status Change Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
                  <Checkbox
                    id="markCompleted"
                    checked={markCompleted}
                    onCheckedChange={setMarkCompleted}
                    data-testid="cad-mark-completed-toggle"
                  />
                  <div>
                    <label htmlFor="markCompleted" className="text-sm font-medium cursor-pointer">Mark as CAD Completed</label>
                    <p className="text-[10px] text-muted-foreground">Instantly change task status to "CAD Completed"</p>
                  </div>
                </div>

                {/* Info */}
                <div className="text-xs text-muted-foreground bg-blue-50 border border-blue-100 rounded-lg p-2.5">
                  {saveMode === 'saveAs'
                    ? `This creates a new CAD sheet for ${sheet.data.styleNo || 'this task'} — a new entry will appear under "CAD Versions" on the task summary, leaving the previous version intact.`
                    : (editingId
                      ? 'This overwrites the open sheet and updates its existing entry on the task summary (no new version is added).'
                      : `This saves the CAD sheet + views and links them to the task summary${linkedTaskId || sheet.data.styleNo ? ` (${sheet.data.styleNo || 'linked task'})` : ''}.`)}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-border">
                <Button variant="ghost" size="sm" onClick={() => setShowSavePopup(false)} className="h-8 text-xs">Cancel</Button>
                <Button size="sm" onClick={handleConfirmSave} disabled={saving} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="cad-confirm-save-btn">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : (saveMode === 'saveAs' ? <FilePlus2 className="h-3.5 w-3.5 mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />)}
                  {saving ? 'Saving...' : (saveMode === 'saveAs' ? 'Save As New' : (editingId ? 'Save' : 'Save & Link'))}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CollapsibleLayout>
  );
}
